import type { ErrorDataUnion, ErrorNames } from '@affine/graphql';
import { GraphQLError as BaseGraphQLError } from 'graphql';

export type ErrorName =
  | keyof typeof ErrorNames
  | 'NETWORK_ERROR'
  | 'CONTENT_TOO_LARGE'
  | 'DOC_UPDATE_TOO_LARGE'
  | 'DOC_SIZE_LIMIT_REACHED'
  | 'DOC_RECOVERY_ONLY'
  | 'DOC_HEALTH_CRITICAL';

export interface UserFriendlyErrorResponse {
  status: number;
  code: string;
  type: string;
  name: ErrorName;
  message: string;
  data?: any;
  stacktrace?: string;
}

const DEFAULT_UNKNOWN_ERROR_MESSAGE =
  'Unhandled error raised. Please contact us for help.';
export const USER_FACING_INTERNAL_ERROR_TITLE = 'Etwas ist schiefgelaufen';
export const USER_FACING_INTERNAL_ERROR_MESSAGE =
  'Das hat nicht geklappt. Bitte versuche es gleich erneut.';

const INTERNAL_SERVER_ERROR_CODE = 'INTERNAL_SERVER_ERROR';
const INTERNAL_ERROR_DETAIL_PATTERN =
  /column .* of relation|ECONNREFUSED|can't reach database server|database server at|PrismaClient|Redis connection|node_modules|\/Users\/|postgres:\d+|localhost:\d+|127\.0\.0\.1:\d+/i;

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function numericStatus(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function originalErrorStatus(
  response: Record<string, unknown>
): number | undefined {
  const originalError = response.originalError;
  if (!originalError || typeof originalError !== 'object') {
    return undefined;
  }
  return (
    numericStatus(
      (originalError as { statusCode?: unknown; status?: unknown }).statusCode
    ) ??
    numericStatus(
      (originalError as { statusCode?: unknown; status?: unknown }).status
    )
  );
}

function normalizeErrorResponse(
  response: Partial<UserFriendlyErrorResponse> & Record<string, unknown>,
  fallbackMessage?: string
): UserFriendlyErrorResponse {
  const code = nonEmptyString(response.code) ?? 'INTERNAL_SERVER_ERROR';
  const type = nonEmptyString(response.type) ?? code;
  const name = (nonEmptyString(response.name) ?? code) as ErrorName;
  const message =
    nonEmptyString(response.message) ??
    nonEmptyString(fallbackMessage) ??
    DEFAULT_UNKNOWN_ERROR_MESSAGE;
  const status =
    numericStatus(response.status) ??
    numericStatus(response.statusCode) ??
    originalErrorStatus(response) ??
    500;

  const normalized = {
    ...response,
    status,
    code,
    type,
    name,
    message,
  };

  if (shouldSanitizeInternalErrorResponse(normalized)) {
    return {
      ...normalized,
      data: undefined,
      message: USER_FACING_INTERNAL_ERROR_MESSAGE,
      stacktrace: undefined,
    };
  }

  return normalized;
}

export function isInternalServerErrorCode(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    value.trim().toUpperCase() === INTERNAL_SERVER_ERROR_CODE
  );
}

export function isInternalServerErrorMessage(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const message = value.trim();
  return (
    /^internal server error$/i.test(message) ||
    message === DEFAULT_UNKNOWN_ERROR_MESSAGE ||
    message === USER_FACING_INTERNAL_ERROR_MESSAGE ||
    INTERNAL_ERROR_DETAIL_PATTERN.test(message)
  );
}

export function isInternalServerErrorResponse(
  response: Partial<UserFriendlyErrorResponse> | null | undefined
): boolean {
  if (!response) return false;
  return (
    isInternalServerErrorCode(response.code) ||
    isInternalServerErrorCode(response.type) ||
    isInternalServerErrorCode(response.name) ||
    isInternalServerErrorMessage(response.message)
  );
}

function shouldSanitizeInternalErrorResponse(
  response: UserFriendlyErrorResponse
): boolean {
  const hasInternalCode =
    isInternalServerErrorCode(response.code) ||
    isInternalServerErrorCode(response.type) ||
    isInternalServerErrorCode(response.name);

  return (
    isInternalServerErrorMessage(response.message) ||
    (hasInternalCode && !nonEmptyString(response.message))
  );
}

function UnknownError(message: string) {
  return new UserFriendlyError(
    normalizeErrorResponse(
      {
        status: 500,
        code: 'INTERNAL_SERVER_ERROR',
        type: 'INTERNAL_SERVER_ERROR',
        name: 'INTERNAL_SERVER_ERROR',
        message,
      },
      DEFAULT_UNKNOWN_ERROR_MESSAGE
    )
  );
}

type ToPascalCase<S extends string> = S extends `${infer A}_${infer B}`
  ? `${Capitalize<Lowercase<A>>}${ToPascalCase<B>}`
  : Capitalize<Lowercase<S>>;

export type ErrorData = {
  [K in ErrorNames]: Extract<
    ErrorDataUnion,
    { __typename?: `${ToPascalCase<K>}DataType` }
  >;
};

export class GraphQLError extends BaseGraphQLError {
  // @ts-expect-error better to be a known type without any type casting
  override extensions!: UserFriendlyErrorResponse;
}

export class UserFriendlyError
  extends Error
  implements UserFriendlyErrorResponse
{
  readonly status = this.response.status;
  readonly code = this.response.code;
  readonly type = this.response.type;
  override readonly name = this.response.name;
  override readonly message = this.response.message;
  readonly data = this.response.data;
  readonly stacktrace = this.response.stacktrace;

  static fromAny(anything: any) {
    if (anything instanceof UserFriendlyError) {
      return anything;
    }

    switch (typeof anything) {
      case 'string':
        return UnknownError(anything);
      case 'object': {
        if (anything) {
          if (anything instanceof GraphQLError) {
            return new UserFriendlyError(
              normalizeErrorResponse(
                {
                  ...(anything.extensions ?? {}),
                  message: anything.extensions?.message ?? anything.message,
                },
                anything.message
              )
            );
          } else if (
            anything.type ||
            anything.code ||
            anything.status ||
            anything.statusCode
          ) {
            return new UserFriendlyError(
              normalizeErrorResponse(anything, anything.message)
            );
          } else if (anything.message) {
            return UnknownError(anything.message);
          }
        }
      }
    }

    return UnknownError(DEFAULT_UNKNOWN_ERROR_MESSAGE);
  }

  constructor(private readonly response: UserFriendlyErrorResponse) {
    super(response.message);
  }

  is(name: ErrorName) {
    return this.name === name;
  }

  isStatus(status: number) {
    return this.status === status;
  }

  static isNetworkError(error: UserFriendlyError) {
    return error.name === 'NETWORK_ERROR';
  }

  static notNetworkError(error: UserFriendlyError) {
    return !UserFriendlyError.isNetworkError(error);
  }

  isNetworkError() {
    return UserFriendlyError.isNetworkError(this);
  }

  notNetworkError() {
    return UserFriendlyError.notNetworkError(this);
  }
}
